import React, { FC, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FontAwesomeIcon as Icon } from '@fortawesome/react-fontawesome';
import { faChevronDown, faCheck, faArrowDown, faArrowUp, faAddressCard, faGlobe, faShapes, faRegistered, faBuilding, faTruckArrowRight } from '@fortawesome/free-solid-svg-icons';
import type { Attendee, Category } from '../../types';
import useApi from '../../hooks/api';
import { useAppDispatch, useAppSelector } from '../../hooks/store';
import getFileURL from '../../utils/getFileURL';
import { useLocation, useNavigate } from 'react-router-dom';
import { attendeesSet } from '../../store/attendeesSlice';
import { attendeeSet } from '../../store/attendeeSlice';
import GeographyFilter from './GeographyFilter';
import { getCountryName } from '../../utils/countries';
import './AttendeeList.css';
import { getProductsType } from '../../utils/attendee';
import ProductsTypeSwicth from '../common/ProductsTypeSwicth';

type Attendees = {
	attendees: Attendee[]
};

export interface AttendeeListProps {
    category: Category|null;
};

const AttendeeList: FC<AttendeeListProps> = ({ category }) => {
	const { t, i18n } = useTranslation();
	const lang = i18n.language === 'en' ? 'en' : 'ru';
	const name = i18n.language === 'en' ? 'name_en' : 'name_ru';
    const company = i18n.language === 'en' ? 'company_en' : 'company_ru';
    const position = i18n.language === 'en' ? 'position_en' : 'position_ru';
    const location = useLocation();
    const user = useAppSelector(store => store.auth.user);
    const event = useAppSelector(store => store.event);
    const requests = useAppSelector(store => store.requests);
    const categories = useAppSelector(store => store.categories);
    const selectedAttendee = useAppSelector(store => store.attendee);
    const navigate = useNavigate();
    const dispatch = useAppDispatch();
    const [ data, error, isLoading ] = useApi('get', '/attendees');
    const [ attendees, setAttendees ] = useState<Attendee[]>([]);
    const [ expanded, setExpanded ] = useState<any>({});
    const [ geography, setGeography ] = useState<''|'federal'|'regional'>('');
    const [ products_type, setProductsType ] = useState([ false, false, false ]);
    // Фильтр и сортировка списка участников с учетом пришедшей в пропсах
    // категории, выбранной географии и собственных категорий пользователя.
    // Нам также надо удалить из списка участников, которые не прошли модерацию,
    // но при условии что с ними уже нет запросов в любом статусе.
    useEffect(() => {
        if(!data) return;
        let attendees = (data as Attendees).attendees;

        // Отфильтруем список
        attendees = attendees.filter(a => {
            // Если участник не отмодерирован, смотрим есть ли запросы с ним
            if(!a.is_approved && !requests.find(r => r.from_id === a.id || r.to_id === a.id)) return false;
            // Остались или отмодерированные или с запросами, фильтруем их по категории и географии
            let c = true;
            let g = true;
            if(category) c = a.categories.includes(category.id);
            if(geography) g = a.geography === geography;
            // Если в мероприятии включена поддержка типа продукции, то фильтруем по ней тоже
            let p = true;
            const [ stm, brand, dist ] = products_type;
            if(event?.use_products_type && (stm || brand || dist)) {
                p = (stm && a.products_type[0]) || (brand && a.products_type[1]) || (dist && a.products_type[2]);
            }
            return c && g && p;
        });

        // Сортируем участников по числу совпадений их категорий с категориями пользователя
        if(user) {
            attendees.sort((a, b) => {
                const isA = a.categories.filter(c => user?.categories.includes(c));
                const isB = b.categories.filter(c => user?.categories.includes(c));
                if(isA.length > isB.length) return -1;
                if(isA.length < isB.length) return 1;
                return a[name].localeCompare(b[name]);
            });
        }

        // Установим данные для отображения
        setAttendees(attendees);

        // Список участников или статус запроса с ними могли измениться
        // после фильтрации или при обновлении списка запросов. Поэтому
        // необходимо сбросить объект expanded. Но просто обнулить его
        // нельзя т.к. запросы проверяются по таймеру и при каждом обновлении
        // список будет автоматически схлопываться. Поэтому нам необходимо
        // пройти по списку и убрать только свернутые элементы.
        // NB: Это какая-то муть! Тут получается постоянный вызов useEffect
        //     т.к. мы устанавливаем expanded и эффект одновременно зависит от него!
        //     При это совершенно не понятно зачем вообще здесь сбрасывать
        //     expanded? Это не имеет никакого смысла.
        // const newExpanded: any = {};
        // for(let key in expanded) {
        //    if(expanded[key]) newExpanded[key] = true;
        // }
        // setExpanded(newExpanded);

    }, [ data, category, user, event, name, geography, requests, products_type ]); //, expanded ]);

    // После получения данных от сервера, заносим участников в стор
    // т.к. информация о них много где используется
	useEffect(() => {
		if(data) {
			dispatch(attendeesSet((data as Attendees).attendees));
		}
	}, [ data, dispatch ]);

    // Вывод списка категорий участника
    const getCategories = (attendee: Attendee) => {
        if(!categories || !attendee.categories.length) return null;
        let list: string[] = [];
        list = attendee.categories.map(category_id => {
            const res = categories.find(c => c.id === category_id);
            if(res) return res[name]; return 'unknown';
        });
        return <div className='IconText'>
            <span><Icon icon={faShapes} fixedWidth/></span>
            <span>{list.join(', ')}</span>
        </div>;
    };

    // Сворачивание/разворачивание элемента списка.
    // Изначально все элементы списка свернуты т.е. раскрывающаяся часть не показывается.
    // При клике на кнопку сворачивания разворачивания мы заносим в объект expanded статус
    // элемента списка - true/false. Далее при конструировании класса элемента списка мы
    // смотрим на состояние элемента - undefined/true/false.
    // Если данный финт не делать, то при первом отображении списка все его элементы будут
    // отображены как развернутые в процессе сворачивания.
    const expandeSwitch = (attendee_id: number) => {
        const newExpanded = { ...expanded };
        const status = newExpanded[attendee_id];
        if(typeof status === 'boolean') {
            newExpanded[attendee_id] = !newExpanded[attendee_id];
        } else {
            newExpanded[attendee_id] = true;
        }
        setExpanded(newExpanded);
    };

    // Индикатор запроса
    const requestIcon = (attendee_id: number) => {
        const req = requests.find(r => r.from_id === attendee_id || r.to_id === attendee_id);
        if(!req || (req.status !== 'approved' && req.status !== 'negotiation')) return null;

        let icon;
        let color = 'darkgoldenrod';
        let bgcolor = 'lightgoldenrodyellow';
        let tooltip = t('search.negotiation');
        if(req.status === 'approved') {
            icon = faCheck;
            color = 'forestgreen';
            bgcolor = 'honeydew';
            tooltip = t('search.approved');
        }
        else if(req.from_id === attendee_id) icon = faArrowDown;
        else icon = faArrowUp;

        return <span className='Tooltip' style={{ color, borderColor: color, backgroundColor: bgcolor }}>
            <Icon className='icon' icon={icon} fixedWidth/>
            <span className='TooltipText'>{tooltip}</span>
        </span>;
    };

    // Клик по списку
    const onClick = (attendee: Attendee) => {
        dispatch(attendeeSet(attendee));
        navigate('attendee');
    };

    // Конструктор класса элемента списка.
    // Проверяем статус элемента - undefined/true/false (см. подробнее выше)
    const getClassName = (attendee: Attendee): string => {
        let status = expanded[attendee.id];
        let className = typeof status === 'boolean' ? (status ? 'item expanded' : 'item collapsed') : 'item';
        // Выбранного участника подсвечиваем только если при этом
        // просматриваем его профайл
        if(attendee.id === selectedAttendee?.id && location.pathname.match(/attendee$/)) className += ' selected';
        return className;
    };

    if(isLoading) return <div>{t('common.loading')}</div>;
    if(error) return <div className='error'>{error.text[lang]}</div>;
    if(!data) return <div className='error'>{t('common.noattendees')}</div>;

    return (
        <div className='AttendeeList'>
            <GeographyFilter onChange={setGeography}/>

            {event?.use_products_type &&
            <ProductsTypeSwicth value={products_type} onChange={setProductsType}/>}

            <p>
                {t('search.attendee_found', { count: attendees.length })}
            </p>
            {attendees.map(attendee => (
                <div 
                    key={attendee.id}
                    className={getClassName(attendee)}
                    onClick={() => onClick(attendee)}
                >
                    <img alt='avatar' className='avatar' src={getFileURL(attendee.avatar)}/>
                    <div className='text'>
                        <div className='main'>
                            <div className='info'>
                                <div className='name'>{attendee[name]}</div>
                                {attendee[company] &&
                                <div className='IconText'>
                                    <span><Icon icon={faBuilding} fixedWidth/></span>
                                    <span>{attendee[company]}</span>
                                </div>}
                            </div>
                            <div className='menu'>
                                {requestIcon(attendee.id)}
                                <span
                                    onClick={e => { e.stopPropagation(); expandeSwitch(attendee.id); }}
                                    className='Tooltip'
                                >
                                    <Icon className='icon' icon={faChevronDown} fixedWidth/>
                                    <span className='TooltipText'>
                                        {expanded[attendee.id] ? t('search.expand') : t('search.collapse') }
                                    </span>
                                </span>
                            </div>
                        </div>
                        <div className='expand'>
                            {attendee[position] &&
                            <div className='IconText'>
                                <span><Icon icon={faAddressCard} fixedWidth/></span>
                                <span>{attendee[position]}</span>
                            </div>}
                            {attendee.country &&
                            <div className='IconText'>
                                <span><Icon icon={faGlobe} fixedWidth/></span>
                                <span>{getCountryName(attendee.country, lang)}</span>
                            </div>}

                            {getCategories(attendee)}

                            {event?.use_products_type &&
                            <div className='IconText'>
                                <span><Icon icon={faRegistered} fixedWidth/></span>
                                <span>{getProductsType(attendee, t)}</span>
                            </div>}

                            {(event?.use_direct_import && attendee.role === 'seller' && attendee.country !== 'RU') &&
                            <div className='IconText'>
                                <span><Icon icon={faTruckArrowRight} fixedWidth/></span>
                                <span>{attendee.direct_import ? t('attendee.direct_import_true') : t('attendee.direct_import_false')}</span>
                            </div>}
                        </div>
                    </div>
                </div>
            ))}
        </div>
	);
};

export default AttendeeList;
